import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StarRating from "./StarRating";
import { toast } from "sonner";

export default function ReviewsSection({ product }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!product?.id) return;
    api.get(`/reviews/${product.id}`).then(r => setReviews(r.data)).catch(() => {});
  }, [product?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to leave a review"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { product_id: product.id, rating, comment });
      const { data } = await api.get(`/reviews/${product.id}`);
      setReviews(data);
      setShowForm(false);
      setComment("");
      setRating(5);
      toast.success("Thank you for your review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 border-t border-[#E6DED5] mt-16" data-testid="reviews-section">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="overline text-[#4A1525] mb-4">Reviews</div>
          <h2 className="font-serif text-4xl mb-6">What she said.</h2>
          {reviews.length > 0 ? (
            <div className="mb-6">
              <div className="font-serif text-5xl mb-2" data-testid="avg-rating">{avg.toFixed(1)}</div>
              <StarRating value={avg} size={18} />
              <div className="overline text-[#5C5C5C] mt-2" data-testid="review-count">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</div>
            </div>
          ) : (
            <p className="text-[#5C5C5C] mb-6">Be the first to share your impressions.</p>
          )}
          <button onClick={() => setShowForm(!showForm)} className="overline underline underline-offset-8 decoration-[#4A1525]" data-testid="write-review-btn">
            {showForm ? "Close" : "Write a review"}
          </button>
        </div>

        <div className="lg:col-span-8">
          {showForm && (
            <form onSubmit={submit} className="border border-[#E6DED5] p-6 mb-8 bg-white space-y-4" data-testid="review-form">
              <div>
                <div className="overline mb-2">Your rating</div>
                <StarRating value={rating} size={22} interactive onChange={setRating} />
              </div>
              <div>
                <div className="overline mb-2">Your thoughts</div>
                <textarea required rows={4} value={comment} onChange={(e) => setComment(e.target.value)} className="w-full border border-[#E6DED5] p-3 text-sm focus:outline-none focus:border-[#4A1525]" placeholder="How does it feel, fit, look?" data-testid="review-comment" />
              </div>
              <button type="submit" disabled={busy} className="bg-[#4A1525] text-[#FAF8F5] px-8 py-3 overline disabled:opacity-50" data-testid="submit-review">
                {busy ? "Sending..." : "Submit review"}
              </button>
            </form>
          )}

          <div className="space-y-6" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.id} className="border-b border-[#E6DED5] pb-6" data-testid={`review-${r.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-serif text-lg">{r.user_name}</div>
                    <StarRating value={r.rating} size={12} />
                  </div>
                  <div className="overline text-[#5C5C5C]">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <p className="text-[#5C5C5C] leading-relaxed mt-2">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
